import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../../../context/navigationContext";
import { Formik } from 'formik';
import * as Yup from 'yup';
import { BackArrow, Input, Button, TopProgress, Toggle, Select } from "../../../components";
import { volumeTypesService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {
  Container,
  HeadWrapper,
  PageTitle,
  FormSection,
  FormRow,
  FormActions,
  ExtraSpecsSection,
  ExtraSpecsTitle,
  ExtraSpecRow,
  ExtraSpecField,
  DeleteButton,
  AddButton,
} from "./styles";

const CreateVolumeType: FC = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  usePageTitle(t("PAGE_TITLE_VOLUME_TYPE_CREATE"));
  const { viewAlert } = useUi();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [regions, setRegions] = useState<any[]>([]);
  const [extraSpecs, setExtraSpecs] = useState<{ key: string; value: string }[]>([]);

  useEffect(() => {
    volumeTypesService.getCreateOptions()
      .then((res) => {
        setRegions(res.data?.regions || []);
        setPageLoading(false);
      })
      .catch((err) => {
        viewAlert({
          severity: 'error',
          message: err?.response?.data?.detail || t('ERROR_OCCURRED'),
        });
        setPageLoading(false);
      });
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string().required(t('REQUIRED_FIELD')),
    description: Yup.string(),
    is_public: Yup.boolean(),
    region_name: Yup.string().required(t('REQUIRED_FIELD')),
  });

  const initialValues = {
    name: '',
    description: '',
    is_public: false,
    region_name: '',
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const extraSpecsObj: Record<string, any> = {};
      extraSpecs.forEach((spec) => {
        if (spec.key && spec.value) {
          extraSpecsObj[spec.key] = spec.value;
        }
      });

      const payload = {
        ...values,
        extra_specs: extraSpecsObj,
      };

      await volumeTypesService.createType(payload);
      viewAlert({
        severity: 'info',
        message: t('VOLUME_TYPE_CREATED'),
      });
      navigate('/openstack/volume-types');
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_OCCURRED'),
      });
    } finally {
      setLoading(false);
    }
  };

  const addExtraSpec = () => {
    setExtraSpecs([...extraSpecs, { key: '', value: '' }]);
  };

  const deleteExtraSpec = (index: number) => {
    setExtraSpecs(extraSpecs.filter((_, i) => i !== index));
  };

  const updateExtraSpec = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...extraSpecs];
    updated[index][field] = value;
    setExtraSpecs(updated);
  };

  if (pageLoading) {
    return <TopProgress loading={pageLoading} />;
  }

  return (
    <Container>
      <BackArrow fallbackPath="/openstack/volume-types" />
      <HeadWrapper>
        <PageTitle>{t('CREATE_VOLUME_TYPE')}</PageTitle>
      </HeadWrapper>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => {
          const formikProps = {
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
          };

          return (
            <div>
              <FormSection>
                <FormRow>
                  <Select
                    required
                    fieldName="region_name"
                    title={t('REGION')}
                    placeholder={t('SELECT_REGION')}
                    data={regions.map((r: any) => ({ id: r.id, label: r.id }))}
                    selectedValue={values.region_name}
                    onChange={(value) => setFieldValue('region_name', value)}
                    errors={errors}
                    touched={touched}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    required
                    fieldName="name"
                    title={t('NAME')}
                    placeholder={t('ENTER_NAME')}
                    {...formikProps}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    fieldName="description"
                    title={t('DESCRIPTION')}
                    placeholder={t('ENTER_DESCRIPTION')}
                    {...formikProps}
                  />
                </FormRow>

                <FormRow>
                  <Toggle
                    value={values.is_public}
                    onChange={() => setFieldValue('is_public', !values.is_public)}
                    title={t('IS_PUBLIC')}
                  />
                </FormRow>
              </FormSection>

              <ExtraSpecsSection>
                <ExtraSpecsTitle>{t('EXTRA_SPECS')}</ExtraSpecsTitle>

                {extraSpecs.length === 0 && (
                  <p>{t('NO_EXTRA_SPECS')}</p>
                )}

                {extraSpecs.map((spec, index) => (
                  <ExtraSpecRow key={index}>
                    <ExtraSpecField>
                      <Input
                        fieldName={`extra_spec_key_${index}`}
                        title={t('PROPERTY_KEY')}
                        placeholder={t('PROPERTY_KEY')}
                        value={spec.key}
                        onChange={(e: any) => updateExtraSpec(index, 'key', e.target.value)}
                      />
                    </ExtraSpecField>
                    <ExtraSpecField>
                      <Input
                        fieldName={`extra_spec_value_${index}`}
                        title={t('PROPERTY_VALUE')}
                        placeholder={t('PROPERTY_VALUE')}
                        value={spec.value}
                        onChange={(e: any) => updateExtraSpec(index, 'value', e.target.value)}
                      />
                    </ExtraSpecField>
                    <DeleteButton onClick={() => deleteExtraSpec(index)}>
                      {t('DELETE')}
                    </DeleteButton>
                  </ExtraSpecRow>
                ))}

                <AddButton onClick={addExtraSpec}>
                  + {t('ADD_VOLUME_TYPE_EXTRA_SPEC')}
                </AddButton>
              </ExtraSpecsSection>

              <FormActions>
                <Button
                  variant="secondary"
                  size="medium"
                  title={t('CANCEL')}
                  onClick={() => navigate('/openstack/volume-types')}
                />
                <Button
                  variant="primary"
                  size="medium"
                  title={t('CREATE')}
                  disabled={loading}
                  loading={loading}
                  onClick={submitForm}
                />
              </FormActions>
            </div>
          );
        }}
      </Formik>
    </Container>
  );
};

export default CreateVolumeType;
